import React, { useState, useEffect } from 'react';
import { Button, Dropdown, Menu, message, Modal, Form, Input, DatePicker, Select } from 'antd';
import { PrinterOutlined, FilePdfOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import UniversalLookup from './UniversalLookup';
import { useTranslation } from 'react-i18next';
import { runReportJob, buildLoadingPageHtml } from '../lib/reportJobs';
import { useAuth } from './AuthContext';

export default function SmartPrintButton({ moduleName, refId, extraParams = {} }) {
    const [templates, setTemplates] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [form] = Form.useForm();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const [lookupOpen, setLookupOpen] = useState(false);
    const [currentLookupField, setCurrentLookupField] = useState(null);
    const [lookupType, setLookupType] = useState('');
    const [lookupDomain, setLookupDomain] = useState('');
    const [lookupFilters, setLookupFilters] = useState({});
    const { user } = useAuth();
    const isSuperAdmin = user?.role?.is_super_admin === true || user?.role?.name === 'SUPER_ADMIN';


    const handleOpenLookup = (paramName, type, domainId = '') => {
        setCurrentLookupField(paramName);
        setLookupType(type || 'sites');
        // 🎯 غير السوبر أدمن يشوف مواقع مؤسسته بس (السيرفر بيتأكد برضو في /reports/jobs)
        setLookupFilters((type || 'sites') === 'sites' && !isSuperAdmin && user?.org_id ? { org_id: user.org_id } : {});
        // domainId هنا هيشيل القيمة زي 'ASSETTYPE'
        setLookupDomain(domainId); 
        setLookupOpen(true);
    };

    const onLookupSelect = (item) => {
        const val = item.value || item.site_id || item.location_id || item.assetnum || item.itemnum || item.location || item.wonum;
        form.setFieldsValue({ [currentLookupField]: val });
        setLookupOpen(false);
    };

    // 1. جلب التمبلت المتاحة
    useEffect(() => {
        const fetchAvailableTemplates = async () => {
            try {
                const res = await axios.get(`/api/reports/available-templates?module=${moduleName}`);
                setTemplates(res.data);
            } catch (err) {
                console.error("Error fetching templates", err);
            }
        };
        fetchAvailableTemplates();
    }, [moduleName]);

    // 2. المحرك الرئيسي لتشغيل التقارير (Universal Runner)
    // 🎯 كان بيفتح /api/reports/render/{id} مباشرة في تاب جديد وياخد الـ PDF
    // فورًا - التوليد ده دلوقتي بيحصل في الخلفية (Celery) عشان ميوقفش السيرفر
    // تحت حمل حقيقي (عشرات المستخدمين بيطبعوا تقارير في نفس الوقت)، فبنفتح
    // التاب فورًا (بلاصة فاضية) وقت الكليك نفسه (عشان الـ popup blocker) وبعدين
    // نوجهه لرابط الـ PDF لما يخلص - شوف runReportJob في lib/reportJobs.js
    const executeReport = async (template, userValues = {}) => {
        if (!template) return;

        // 1. تجهيز نسخة من الـ extraParams
        let cleanedExtraParams = { ...extraParams };

        // 2. 🎯 السحر هنا: لو التقرير Summary، بنعمل "تصفير" للبارامترات اللي موجودة في المودال
        // عشان لو اليوزر سابها فاضية، مياخدش القيمة اللي في خلفية الشاشة (الـ refId أو الـ location)
        if (template.is_summary && template.parameters_config) {
            template.parameters_config.forEach(p => {
                // أي حقل معروض لليوزر في المودال (dropdown, date, input)
                // لو ملوش قيمة في userValues، بنشيله من الـ extraParams
                if (p.type !== 'hidden' && !userValues[p.name]) {
                    delete cleanedExtraParams[p.name];
                    // بنمسح المسميات الشائعة برضه للتأكيد
                    if (p.name === 'location') delete cleanedExtraParams['location'];
                    if (p.name === 'asset_num') delete cleanedExtraParams['asset_num'];
                }
            });
            // في الـ Summary غالباً بنشيل الـ ref_id الأساسي لأنه بيحصر الداتا في سجل واحد
            delete cleanedExtraParams['ref_id'];
        }

        // 3. الـ refId لو تقرير تفصيلي (Detail) وفيه قيمة
        const effectiveRefId = (!template.is_summary && refId) ? refId : null;

        // 4. البارامترات المخفية (Hidden/Group By)
        const hiddenParams = {};
        if (template.parameters_config) {
            template.parameters_config.forEach(p => {
                if (p.type === 'hidden' || p.name === 'group_by') {
                    hiddenParams[p.name] = p.default;
                }
            });
        }

        // 5. الدمج النهائي (الأولوية لمدخلات اليوزر ثم الـ extra المفلترة)
        const combined = { ...cleanedExtraParams, ...hiddenParams, ...userValues };

        const finalParams = {};
        Object.keys(combined).forEach(key => {
            let val = combined[key];
            if (val !== undefined && val !== null && val !== '') {
                // 🎯 الـ DatePicker الحالي بيرجّع dayjs مش moment - الشرط القديم
                // (_isAMomentObject) ماكانش بيمسكه، فالتاريخ كان بيتبعت ISO بتوقيت
                // UTC (2026-05-31T21:00:00.000Z بدل 2026-06-01)
                if (val && typeof val === 'object' && typeof val.format === 'function') val = val.format('YYYY-MM-DD');
                finalParams[key] = val;
            }
        });

        setIsModalVisible(false);

        const loadingTab = window.open('', '_blank');
        if (loadingTab) {
            loadingTab.document.write(buildLoadingPageHtml(i18n.language));
        }
        try {
            const pdfUrl = await runReportJob({ template_id: template.id, ref_id: effectiveRefId, params: finalParams });
            if (loadingTab && !loadingTab.closed) {
                loadingTab.location.href = pdfUrl;
            } else {
                window.open(pdfUrl, '_blank');
            }
        } catch (err) {
            if (loadingTab) loadingTab.close();
            message.error(err.message || 'Report generation failed');
        }
    };

    // 3. معالجة الضغط من القائمة
    const handleMenuClick = (template) => {
        // فحص: هل فيه بارامترات محتاجة إدخال (غير الـ hidden و group_by)؟
        const needsInput = template.parameters_config?.some(p => p.type !== 'hidden' && p.name !== 'group_by');

        if (needsInput) {
            setSelectedTemplate(template);
            setIsModalVisible(true);
            form.resetFields();
        } else {
            executeReport(template);
        }
    };

    return (
        <>
            <Dropdown overlay={
                <Menu>
                    {templates.length > 0 ? templates.map(tpl => (
                        <Menu.Item key={tpl.id} icon={<FilePdfOutlined />} onClick={() => handleMenuClick(tpl)}>
                            {tpl.name}
                        </Menu.Item>
                    )) : <Menu.Item disabled className={`text-[11px] font-black uppercase ${isRTL ? "font-['Cairo']" : 'font-sans'}`}>{t('no_templates')}</Menu.Item>}
                </Menu>
            } trigger={['click']}>
                <Button icon={<PrinterOutlined />} type="text"
                  className={`flex items-center gap-1.5 px-3 h-8 text-blue-200 hover:text-white hover:bg-white/10 border-0 `}
                  style={{ width: 'fit-content' }}
                >
                  <span className={`text-[11px] font-black uppercase ${isRTL ? "font-['Cairo']" : 'font-sans'}`}> {t('rpt_print')}</span>
                </Button>
            </Dropdown>

            <Modal
                title={`Report Settings: ${selectedTemplate?.name}`}
                visible={isModalVisible}
                zIndex={1000}
                getContainer={false}
                onOk={() => form.submit()}
                onCancel={() => setIsModalVisible(false)}
                okText="Generate Report"
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={(values) => executeReport(selectedTemplate, values)}>
                    {selectedTemplate?.parameters_config?.filter(p => p.name !== 'group_by' && p.type !== 'hidden').map(p => (
                        <Form.Item key={p.name} name={p.name} label={p.label}>
                            {p.type === 'date' ? (
                                <DatePicker style={{ width: '100%' }} />
                            ): p.type === 'domain' ? (
                                <Input 
                                    readOnly 
                                    suffix={
                                        <SearchOutlined 
                                            className="cursor-pointer text-blue-600" 
                                            // 🎯 التعديل هنا: النوع 'DOMAIN' واسم الدومين يجي من p.lookupType أو p.domain_id
                                            onClick={() => handleOpenLookup(p.name, 'DOMAIN', p.lookupType.toUpperCase())} 
                                        />
                                    } 
                                    placeholder={`Select ${p.label}...`}
                                />
                            
                            ) : p.type === 'dropdown' ? (
                                <Input 
                                    readOnly 
                                    suffix={<SearchOutlined className="cursor-pointer text-blue-600" onClick={() => handleOpenLookup(p.name, p.lookupType)} />} 
                                    placeholder={`Select ${p.label}...`}
                                />
                            ) : (
                                <Input placeholder={`Enter ${p.label}...`} />
                            )}
                        </Form.Item>
                    ))}
                </Form>
            </Modal>
            
            <UniversalLookup 
                isOpen={lookupOpen}
                onClose={() => setLookupOpen(false)}
                title={`Select ${currentLookupField}`}
                type={lookupType}
                filters={lookupFilters}
                domainId={lookupDomain} // 🎯 تأكد من تمرير هذا البروب
                onSelect={onLookupSelect}
                style={{ zIndex: 9999999 }} 
                zIndex={9999999}
            />
        </>    
    );
}